import { Button, FormLabel, Grid, IconButton, Stack, TextField, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { DeleteOutline as DeleteIcon, Plus as PlusIcon } from 'mdi-material-ui';
import { useState } from 'react';
import { useChartsTheme } from '@/components';
import { TableThresholdBaseOption, TableThresholdOptions, TableThresholdStepOption } from '@/core';
import { Color, DEFAULT_STEP, TextAndBackgroundColorPicker, WithKey } from '@/plugin-editor-components';

/**
 * Sort steps from smallest value to largest value. Operates on steps with keys AND steps without keys.
 */
function sortSteps<T extends TableThresholdStepOption>(steps?: T[]) {
  return [...(steps ?? [])].sort((a, b) => a.value - b.value);
}

/**
 * TableColumnThresholdsEditor
 */
export function TableColumnThresholdsEditor({
  thresholds,
  onChange,
}: {
  thresholds?: TableThresholdOptions;
  onChange: (thresholds: TableThresholdOptions) => void;
}) {
  const base = thresholds?.base;
  const steps = thresholds?.steps;

  // Locally, we need to store a key with each step to identify it,
  // so that we can add steps, remove steps, but still keep the list of steps sorted by value.
  const [localSteps, setLocalSteps] = useState<Array<WithKey<TableThresholdStepOption>>>(
    steps ? sortSteps(steps).map((step, index) => ({ ...step, key: `${index}` })) : []
  );

  const hasSteps = localSteps?.length > 0;
  const smallestStep = hasSteps ? localSteps[0] : undefined;
  const largestStep = hasSteps ? localSteps[localSteps.length - 1] : undefined;

  // Handles two things before passing to onChange to update the spec:
  // 1 - Sorting steps
  // 2 - Remove keys from steps
  const onChangeSteps = (newSteps: Array<WithKey<TableThresholdStepOption>>) => {
    const sortedSteps = sortSteps(newSteps);
    setLocalSteps(sortedSteps);

    const keylessSteps: TableThresholdStepOption[] = sortedSteps?.map((stepWithKey) => {
      const { key: _key, ...step } = stepWithKey;
      return step;
    });

    onChange(
      produce(thresholds ?? {}, (draft) => {
        draft.steps = keylessSteps;

        if (keylessSteps.length === 0) {
          delete draft.steps;
        }
      })
    );
  };

  const handleStepAdd = () => {
    onChangeSteps(
      produce(localSteps, (draft) => {
        draft.push({
          value: (largestStep?.value ?? 0) + DEFAULT_STEP,
          key: `${Number(largestStep?.key ?? 0) + 1}`,
        });
      })
    );
  };

  const handleStepChange = (newStep: WithKey<TableThresholdStepOption>, key: string) => {
    onChangeSteps(
      produce(localSteps, (draft) => {
        const index = draft.findIndex((s) => s.key === key);
        if (draft[index]) {
          draft[index] = newStep;
        }
      })
    );
  };

  const handleStepDelete = (key: string): void => {
    onChangeSteps(
      produce(localSteps, (draft) => {
        const index = draft.findIndex((s) => s.key === key);
        if (draft[index]) {
          draft.splice(index, 1);
        }
      })
    );
  };

  const handleBaseChange = (newBase: TableThresholdBaseOption) => {
    onChange(
      produce(thresholds ?? {}, (draft) => {
        draft.base = newBase;
      })
    );
  };

  return (
    <Stack spacing={1}>
      <Grid container spacing={0} alignItems="center">
        {localSteps
          .map((step) => {
            return (
              <TableColumnThresholdStep
                key={step.key}
                step={step}
                onStepChange={handleStepChange}
                onStepDelete={handleStepDelete}
              />
            );
          })
          .reverse()}
        <TableColumnThresholdBase base={base ?? {}} smallestStep={smallestStep} onBaseChange={handleBaseChange} />
      </Grid>

      <Button variant="secondary" StartIcon={<PlusIcon />} sx={{ alignSelf: 'start' }} onClick={handleStepAdd}>
        Add rule
      </Button>
    </Stack>
  );
}

function TableColumnThresholdStep({
  step,
  onStepChange,
  onStepDelete,
}: {
  step: WithKey<TableThresholdStepOption>;
  onStepChange: (newStep: WithKey<TableThresholdStepOption>, key: string) => void;
  onStepDelete: (key: string) => void;
}) {
  const defaultTextColor = useDefaultTextColor();

  const valueInputId = `step-${step.key}-value`;
  const [localValue, setLocalValue] = useState(step.value === 0 ? '' : String(step.value));
  const [localMessage, setLocalMessage] = useState(step.name);

  // Don't update until blur or pressing "Enter".
  // Otherwise, the steps could reorder while the user is in the middle of typing.
  const handleValueSubmit = () => {
    onStepChange(
      produce(step, (draft) => {
        draft.value = Number(localValue);
      }),
      step.key
    );
  };

  const handleColorChange = (newColor: Color) => {
    onStepChange(
      produce(step, (draft) => {
        draft.color = newColor.color;
        draft.background_color = newColor.background;

        if (draft.color === undefined) {
          delete draft.color;
        }
        if (draft.background_color === undefined) {
          delete draft.background_color;
        }
      }),
      step.key
    );
  };

  // Don't update until blur or pressing "Enter".
  const handleMessageSubmit = () => {
    onStepChange(
      produce(step, (draft) => {
        draft.name = localMessage;

        if (draft.name === '') {
          delete draft.name;
        }
      }),
      step.key
    );
  };

  return (
    <>
      <Grid item xs={2} justifySelf="start">
        <FormLabel htmlFor={valueInputId}>
          <Typography noWrap>When value is &gt;=</Typography>
        </FormLabel>
      </Grid>

      {/* Value */}
      <Grid item xs={4}>
        <TextField
          id={valueInputId}
          fullWidth
          type="number"
          value={localValue}
          placeholder="0"
          onChange={(e) => setLocalValue(e.currentTarget.value)}
          onBlur={handleValueSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleValueSubmit();
            }
          }}
        />
      </Grid>

      {/* Color */}
      <Grid item xs={1} display="flex" justifyContent="center">
        <TextAndBackgroundColorPicker
          onColorChange={handleColorChange}
          color={{ color: step.color ?? defaultTextColor, background: step.background_color }}
        />
      </Grid>

      {/* Message */}
      <Grid item xs={4}>
        <TextField
          fullWidth
          label="Message"
          value={localMessage}
          onChange={(e) => setLocalMessage(e.currentTarget.value)}
          onBlur={handleMessageSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleMessageSubmit();
            }
          }}
        />
      </Grid>

      {/* Delete */}
      <Grid item xs={1} display="flex" justifyContent="center">
        <Tooltip Content="Delete">
          <IconButton variant="tertiary" size="small" onClick={() => onStepDelete(step.key)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </>
  );
}

function TableColumnThresholdBase({
  base,
  smallestStep,
  onBaseChange,
}: {
  base: TableThresholdBaseOption;
  smallestStep?: TableThresholdStepOption;
  onBaseChange: (base: TableThresholdBaseOption) => void;
}) {
  const defaultTextColor = useDefaultTextColor();
  const hasSteps = smallestStep !== undefined;

  const messageInputId = 'base-message';
  const [localMessage, setLocalMessage] = useState(base.name);

  const handleColorChange = (newColor: Color) => {
    onBaseChange(
      produce(base, (draft) => {
        draft.color = newColor.color;
        draft.background_color = newColor.background;

        if (draft.color === undefined) {
          delete draft.color;
        }
        if (draft.background_color === undefined) {
          delete draft.background_color;
        }
      })
    );
  };

  // Don't update until blur or pressing "Enter".
  const handleMessageSubmit = () => {
    onBaseChange(
      produce(base, (draft) => {
        draft.name = localMessage;

        if (draft.name === '') {
          delete draft.name;
        }
      })
    );
  };

  return (
    <>
      <Grid item xs={2}>
        <FormLabel htmlFor={messageInputId}>
          {hasSteps ? (
            <Typography noWrap>When value is &lt; {smallestStep.value}</Typography>
          ) : (
            <Typography noWrap>Base settings</Typography>
          )}
        </FormLabel>
      </Grid>

      {hasSteps && <Grid item xs={4} />}

      <Grid item xs={1} display="flex" justifyContent="center">
        <TextAndBackgroundColorPicker
          onColorChange={handleColorChange}
          color={{
            color: base?.color ?? defaultTextColor,
            background: base?.background_color,
          }}
        />
      </Grid>

      <Grid item xs={4}>
        <TextField
          id={messageInputId}
          fullWidth
          label="Message"
          value={localMessage}
          onChange={(e) => setLocalMessage(e.currentTarget.value)}
          onBlur={handleMessageSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleMessageSubmit();
            }
          }}
        />
      </Grid>

      {hasSteps && <Grid item xs={1} />}
    </>
  );
}

function useDefaultTextColor() {
  return useChartsTheme().theme.palette.text.primary;
}
